import { useState, useEffect } from 'react'
import { Box, Chip, Tabs, Tab, Alert } from '@mui/material'
import { Add, Warning, Edit, Delete, Science, Build, Inventory2 } from '@mui/icons-material'
import { PageHeader, Button, DataTable, Card, Modal, PermissionGate } from '@components/common'
import { InventoryForm } from '@components/inventory'
import { usePermissions } from '@context/PermissionContext'
import { toast } from 'react-toastify'
import api from '@services/api'

const INVENTORY_TYPES = [
  { value: '', label: 'All Items', icon: <Inventory2 /> },
  { value: 'spare_part', label: 'Spare Parts', icon: <Build /> },
  { value: 'reagent', label: 'Reagents', icon: <Science /> },
  { value: 'consumable', label: 'Consumables', icon: <Inventory2 /> },
  { value: 'accessory', label: 'Accessories', icon: <Inventory2 /> },
]

const columns = [
  {
    field: 'sn',
    headerName: 'SN',
    width: 50,
    renderCell: (_, row, index) => index + 1
  },
  { field: 'part_number', headerName: 'Part #', sortable: true, width: 100 },
  { field: 'name', headerName: 'Name', sortable: true },
  {
    field: 'inventory_type',
    headerName: 'Type',
    renderCell: (v) => (
      <Chip
        label={INVENTORY_TYPES.find((t) => t.value === v)?.label || v || 'Spare Part'}
        size="small"
        variant="outlined"
        sx={{ textTransform: 'capitalize' }}
      />
    ),
  },
  { field: 'category_name', headerName: 'Category', sortable: true, renderCell: (v) => v || '-' },
  { field: 'department_name', headerName: 'Department', sortable: true, renderCell: (v) => v || '-' },
  { field: 'manufacturer', headerName: 'Manufacturer', sortable: true, renderCell: (v) => v || '-' },
  {
    field: 'current_stock',
    headerName: 'In Stock',
    sortable: true,
    align: 'right',
  },
  { field: 'reorder_point', headerName: 'Reorder Pt', sortable: true, align: 'right', width: 90 },
  {
    field: 'stock_status',
    headerName: 'Status',
    renderCell: (_, row) => {
      const isLow = row.is_low_stock || (row.current_stock <= row.reorder_point)
      return (
        <Chip
          label={isLow ? 'Low Stock' : 'In Stock'}
          size="small"
          color={isLow ? 'error' : 'success'}
          icon={isLow ? <Warning fontSize="small" /> : undefined}
        />
      )
    },
  },
  { field: 'location', headerName: 'Location', sortable: true, renderCell: (v) => v || '-' },
  {
    field: 'expiry_date',
    headerName: 'Expiry',
    renderCell: (v) => {
      if (!v) return '-'
      const date = new Date(v)
      const today = new Date()
      const daysUntil = Math.ceil((date - today) / (1000 * 60 * 60 * 24))
      const isExpired = daysUntil < 0
      const isExpiringSoon = daysUntil >= 0 && daysUntil <= 30
      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          {date.toLocaleDateString()}
          {isExpired && <Chip label="Expired" size="small" color="error" />}
          {isExpiringSoon && !isExpired && <Chip label={`${daysUntil}d`} size="small" color="warning" />}
        </Box>
      )
    },
  },
  {
    field: 'unit_cost',
    headerName: 'Unit Cost',
    sortable: true,
    align: 'right',
    renderCell: (value) => value ? `UGX ${Number(value).toLocaleString()}` : '-',
  },
]

const InventoryList = () => {
  const [parts, setParts] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [typeFilter, setTypeFilter] = useState('')
  const { canEdit, canDelete } = usePermissions()

  const fetchParts = async () => {
    try {
      setLoading(true)
      const params = typeFilter ? { inventory_type: typeFilter } : {}
      const response = await api.get('/inventory/spare-parts/', { params })
      setParts(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching inventory:', error)
      toast.error('Failed to load inventory items')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchParts()
  }, [typeFilter])

  // Calculate stats
  const lowStockCount = parts.filter((p) => p.is_low_stock || p.current_stock <= p.reorder_point).length
  const expiringCount = parts.filter((p) => {
    if (!p.expiry_date) return false
    const daysUntil = Math.ceil((new Date(p.expiry_date) - new Date()) / (1000 * 60 * 60 * 24))
    return daysUntil >= 0 && daysUntil <= 30
  }).length

  const handleFormSuccess = () => {
    fetchParts()
    setSelectedItem(null)
  }

  const handleEdit = (item) => {
    setSelectedItem(item)
    setFormOpen(true)
  }

  const handleDelete = async () => {
    if (!selectedItem) return
    setDeleting(true)
    try {
      await api.delete(`/inventory/spare-parts/${selectedItem.id}/`)
      toast.success('Item deleted successfully')
      fetchParts()
      setDeleteModalOpen(false)
      setSelectedItem(null)
    } catch (error) {
      console.error('Error deleting item:', error)
      toast.error('Failed to delete item')
    } finally {
      setDeleting(false)
    }
  }

  const actionsColumn = {
    field: 'actions',
    headerName: '',
    width: 100,
    renderCell: (_, row) => (
      <Box display="flex" gap={1}>
        {canEdit('inventory') && (
          <Button
            size="small"
            variant="text"
            onClick={(e) => {
              e.stopPropagation()
              handleEdit(row)
            }}
          >
            <Edit fontSize="small" />
          </Button>
        )}
        {canDelete('inventory') && (
          <Button
            size="small"
            variant="text"
            color="error"
            onClick={(e) => {
              e.stopPropagation()
              setSelectedItem(row)
              setDeleteModalOpen(true)
            }}
          >
            <Delete fontSize="small" />
          </Button>
        )}
      </Box>
    ),
  }

  return (
    <Box>
      <PageHeader
        title="Inventory"
        subtitle="Manage spare parts, reagents, and supplies"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Inventory' },
        ]}
        actions={
          <PermissionGate permission="inventory.create">
            <Button startIcon={<Add />} onClick={() => setFormOpen(true)}>
              Add Item
            </Button>
          </PermissionGate>
        }
      />

      {lowStockCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<Warning />}>
          {lowStockCount} item(s) are low on stock and need to be reordered
        </Alert>
      )}

      {expiringCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {expiringCount} item(s) expiring within 30 days
        </Alert>
      )}

      <Card>
        <Tabs
          value={typeFilter}
          onChange={(_, v) => setTypeFilter(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {INVENTORY_TYPES.map((type) => (
            <Tab
              key={type.value}
              value={type.value}
              label={type.label}
              icon={type.icon}
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
          ))}
        </Tabs>

        <DataTable
          columns={[...columns, actionsColumn]}
          data={parts}
          loading={loading}
          searchPlaceholder="Search inventory..."
          emptyMessage="No items found"
        />
      </Card>

      <InventoryForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false)
          setSelectedItem(null)
        }}
        item={selectedItem}
        onSuccess={handleFormSuccess}
      />

      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Spare Part"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button color="error" onClick={handleDelete} loading={deleting}>
              Delete
            </Button>
          </>
        }
      >
        Are you sure you want to delete "{selectedItem?.name}"?
      </Modal>
    </Box>
  )
}

export default InventoryList
